import moment from "moment";
import {
  checkIfIsBusinessDay,
  getNextWorkingDay,
} from "../../../utilities/utilities";
import { Collection } from "../../../Models/Collection";

/** List of statuses that allow a collection to be deleted */
const statusEnabledForDeletion = ["DRAFT"];

/** List of statuses that allow a collection to be booked */
const statusEnabledForBooking = ["DRAFT"];

/**
 * Checks if a single collection can be cancelled based on its status.
 * * @param status The current status of the collection.
 * @returns True if the status is included in the enabled deletion list.
 */
export const isCollectionCancellable = (status: string): boolean => {
  return statusEnabledForDeletion.includes(status);
};

/**
 * Checks if a list of collections can be cancelled.
 * * @param list The array of collection objects.
 * @returns True if the list is not empty and at least one item is cancellable.
 */
export const isCollectionsCancellable = (list: Collection[]): boolean => {
  return (
    list.length > 0 &&
    !list.every((item) => isCollectionCancellable(item.status))
  );
};

/**
 * Checks if a single collection can be booked based on its status.
 * * @param status The current status of the collection.
 * @returns True if the status is included in the enabled booking list.
 */
export const isCollectionBookable = (status: string): boolean => {
  return statusEnabledForBooking.includes(status);
};

/**
 * Checks if a list of collections can be booked.
 * * @param list The array of collection objects.
 * @returns True if the list is not empty and at least one item is bookable.
 */
export const isCollectionsBookable = (list: Collection[]): boolean => {
  return (
    list.length > 0 && !list.every((item) => isCollectionBookable(item.status))
  );
};

/**
 * Calculates the collection date based on a cutoff time
 * and business-day logic.
 *
 * Behavior:
 * - If a `time` cutoff is provided and the current time is **after** the cutoff,
 * the function returns **the next working day**.
 * - If today is **not a business day**, the function also returns
 * **the next working day**, regardless of the cutoff.
 * - Otherwise, if the current time is **before** the cutoff and today is a business day,
 * it returns **the current date**.
 *
 * @param {string | undefined | null} time
 * Optional cutoff time in `"HH:mm"` format.
 *
 * @returns {Date}
 * The computed collection date.
 */
export const calculateCollectionDate = (time?: string | null): Date => {
  const now = moment();

  // If a cutoff time is provided
  if (time) {
    const cutoff = moment(time, "HH:mm");
    const isAfterCutoff = now.isAfter(cutoff);

    // If it's after cutoff OR today is not a business day → move to next working day
    if (isAfterCutoff || !checkIfIsBusinessDay()) {
      return getNextWorkingDay();
    }
  }

  // If before cutoff AND today is a working day → today
  return now.toDate();
};
